import { ChangeEvent, Dispatch, FC, useContext, useEffect } from 'react';
import { Box, Button, Pagination } from '@mui/material';
import PackageCard from './PackageCard';
import { MAX_PACKAGES_PAGE_LIMIT } from '../../constants/numbers';
import { useTranslation } from 'react-i18next';
import { CampaignContext } from '../../contexts/CampaignContext';
import { IPackage } from '../../interfaces/campaign';

type IPackagesList = {
	searchTerm: string;
	onSave: () => void;
	onCancel: () => void;
	setShowPages?: Dispatch<React.SetStateAction<number | null>>;
};

const PackagesList: FC<IPackagesList> = ({ searchTerm, onSave, onCancel, setShowPages }) => {
	const { fetchedPackages, fetchedPackagesPagination, selectedPackages, packagePage, viewSelectedItemsMode, setSelectedPackages, setPackagePage } = useContext(CampaignContext);

	const { t } = useTranslation();

	const handleSelect = (item: IPackage, checked: boolean) => {
		if (checked) {
			setSelectedPackages([...selectedPackages, item]);
		} else {
			setSelectedPackages(selectedPackages.filter(pkg => pkg.id !== item?.id));
		}
	};

	const handleChangePage = (_: ChangeEvent<unknown>, newPage: number) => {
		setPackagePage(newPage);
	};

	const handleCancel = () => {
		setSelectedPackages([]);
		onCancel();
	};

	const totalItems = fetchedPackagesPagination?.total;

	// Reset page if the current page exceeds the total pages after filtering
	useEffect(() => {
		if (packagePage > Math.ceil(totalItems! / MAX_PACKAGES_PAGE_LIMIT)) {
			setPackagePage(1);
		}
	}, [searchTerm, packagePage, MAX_PACKAGES_PAGE_LIMIT, totalItems]);

	return (
		<Box className="flex flex-col justify-center">
			<Box className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-6">{(viewSelectedItemsMode ? selectedPackages : fetchedPackages)?.map((pkg: IPackage) => <PackageCard key={pkg.id} {...pkg} onSelect={handleSelect} setShowPages={setShowPages} />)}</Box>
			{!viewSelectedItemsMode && (
				<Box className="mt-8 flex w-full items-center justify-between">
					{(totalItems ?? 0) > MAX_PACKAGES_PAGE_LIMIT ? <Pagination count={Math.ceil(totalItems! / MAX_PACKAGES_PAGE_LIMIT)} page={packagePage} onChange={handleChangePage} variant="text" showFirstButton={false} showLastButton={false} className="flex justify-center" size="medium" shape="rounded" color="primary" /> : <div />}
					<Box className="flex items-center gap-x-5">
						<Button className="h-10 w-48" disabled={selectedPackages.length === 0} onClick={onSave}>
							{t('register')}
						</Button>
						<Button className="h-10 w-48" variant="outlined" onClick={handleCancel}>
							{t('cancel')}
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default PackagesList;
